/**
 * API Service - Custom API calls for endpoints not covered by React Admin data provider
 *
 * Following React Admin best practices:
 * - https://marmelab.com/react-admin/DataFetchingGuide.html#custom-data-provider-methods
 * - Use with useDataProvider() hook or useMutation() from TanStack Query
 */

import { API_URL, httpClient } from '../httpClient';
import { fileApi, fileDataProviderMethods } from './file.service';

// ============================================
// TYPES
// ============================================

export enum TicketListTab {
  ATTENDING = 'attending',
  WAITING = 'waiting',
  CLOSED = 'closed',
  SEARCH = 'search',
}

export interface TicketByTabRequest {
  tab: TicketListTab;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
  searchQuery?: string;
}

export interface TransferTicketRequest {
  toUserId?: number;
  toQueueId?: number;
}

export interface CreateTicketFromContactRequest {
  channelConnectionId: number;
  contactId: number;
  phoneNumber?: string;
  ticketQueueId?: number;
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  initialMessage?: string;
}

export interface TicketSettings {
  autoReopenTimeoutMinutes: number;
}

export interface UpdateTicketSettingsRequest {
  autoReopenTimeoutMinutes?: number;
}

export interface ZapyInstanceStatus {
  status: string;
  qrCode?: string;
  profileName?: string;
  ownerJid?: string;
}

export interface ZapyQRCode {
  qrCode: string;
  expiresAt: number;
}

export interface UpdateChannelConnectionRequest {
  name?: string;
  ticketConfig?: { mode: 'auto' | 'manual' | 'off' } | null;
  defaultQueueId?: number | null;
  config?: {
    ignoreGroupMessages?: boolean;
    notifyGroupMessages?: boolean;
  } | null;
  credentials?: Record<string, unknown>;
}

export interface VerifyEmailConnectionResult {
  ok: boolean;
  smtpVerified: boolean;
  imapVerified: boolean;
  error?: string;
}

export interface TelegramBotStatus {
  status: string;
  botId: number;
  botUsername?: string;
  botFirstName: string;
  canJoinGroups: boolean;
  canReadAllGroupMessages: boolean;
  supportsInlineQueries: boolean;
  webhookActive?: boolean;
  lastWebhookError?: string;
  lastWebhookErrorDate?: number;
  pendingUpdateCount?: number;
}

export interface CreateChannelConnectionRequest {
  channelId: number;
  name: string;
  credentials?: Record<string, unknown>;
}

export interface ChannelConnectionResponse {
  id: number;
  uuid: string;
  channelId: number;
  name: string;
  externalId?: string | null;
  status: string;
  metadata?: Record<string, unknown> | null;
  ticketConfig?: { mode: 'auto' | 'manual' | 'off' } | null;
  defaultQueueId?: number | null;
  config?: {
    ignoreGroupMessages?: boolean;
    notifyGroupMessages?: boolean;
  } | null;
  createdAt: string;
  updatedAt: string;
}

// ============================================
// HTTP HELPERS (DRY)
// ============================================

const api = {
  get: async <T>(path: string): Promise<T> => {
    const { json } = await httpClient(`${API_URL}/${path}`, { method: 'GET' });
    return json as T;
  },

  post: async <T>(path: string, data?: unknown): Promise<T> => {
    const options: RequestInit = { method: 'POST' };
    if (data !== undefined) {
      options.body = JSON.stringify(data);
    }
    const { json } = await httpClient(`${API_URL}/${path}`, options);
    return json as T;
  },

  patch: async <T>(path: string, data?: unknown): Promise<T> => {
    const options: RequestInit = { method: 'PATCH' };
    if (data !== undefined) {
      options.body = JSON.stringify(data);
    }
    const { json } = await httpClient(`${API_URL}/${path}`, options);
    return json as T;
  },

  delete: async <T>(path: string, data?: unknown): Promise<T> => {
    const options: RequestInit = { method: 'DELETE' };
    if (data !== undefined) {
      options.body = JSON.stringify(data);
    }
    const { json } = await httpClient(`${API_URL}/${path}`, options);
    return json as T;
  },
};

// ============================================
// TICKET CUSTOM METHODS
// ============================================

export const ticketApi = {
  /** List tickets by tab (attending/waiting/closed/search) */
  listByTab: (params: TicketByTabRequest) =>
    api.post('ticket/by-tab', {
      tab: params.tab,
      page: params.page ?? 1,
      limit: params.limit ?? 20,
      sortBy: params.sortBy,
      sortOrder: params.sortOrder,
      searchQuery: params.searchQuery,
    }),

  /** Transfer ticket to another user or queue */
  transfer: (ticketId: number, data: TransferTicketRequest) =>
    api.post(`ticket/${ticketId}/transfer`, data),

  /** Return ticket to pending (back to queue) */
  returnToPending: (ticketId: number) =>
    api.post(`ticket/${ticketId}/return-to-pending`),

  /** Spy on ticket (view without marking as read) */
  spy: (ticketId: number) =>
    api.get(`ticket/${ticketId}/spy`),

  /** Create ticket from contact (validates WhatsApp number) */
  createFromContact: (data: CreateTicketFromContactRequest) =>
    api.post('ticket/from-contact', data),

  /** Close ticket */
  close: (ticketId: number) =>
    api.post(`ticket/${ticketId}/close`),

  /** Reopen closed ticket */
  reopen: (ticketId: number) =>
    api.post(`ticket/${ticketId}/reopen`),

  /** Resolve ticket */
  resolve: (ticketId: number) =>
    api.post(`ticket/${ticketId}/resolve`),

  /** Get ticket settings */
  getSettings: () =>
    api.get<{ data: TicketSettings }>('ticket/settings'),

  /** Update ticket settings */
  updateSettings: (data: UpdateTicketSettingsRequest) =>
    api.patch<{ data: TicketSettings }>('ticket/settings', data),
};

// ============================================
// CHANNEL CONNECTION CUSTOM METHODS (ZAPY)
// ============================================

export const channelConnectionApi = {
  /** Create a new channel connection */
  create: (data: CreateChannelConnectionRequest) =>
    api.post<{ data: ChannelConnectionResponse }>('channel-connection', data),

  /** Get Zapy instance status (includes QR code if pending) */
  getZapyStatus: (channelConnectionId: number) =>
    api.get<{ data: ZapyInstanceStatus }>(`channel-connection/${channelConnectionId}/zapy/status`),

  /** Get fresh QR code for Zapy instance */
  getZapyQRCode: (channelConnectionId: number) =>
    api.get<{ data: ZapyQRCode }>(`channel-connection/${channelConnectionId}/zapy/qr-code`),

  /** Restart Zapy instance connection */
  restartZapyInstance: (channelConnectionId: number) =>
    api.post<{ data: { ok: true } }>(`channel-connection/${channelConnectionId}/zapy/restart`),

  /** Logout/disconnect Zapy instance */
  logoutZapyInstance: (channelConnectionId: number) =>
    api.post<{ data: { ok: true } }>(`channel-connection/${channelConnectionId}/zapy/logout`),

  /** Get live Telegram bot status (calls Telegram API) */
  getTelegramBotStatus: (channelConnectionId: number) =>
    api.get<{ data: TelegramBotStatus }>(`channel-connection/${channelConnectionId}/telegram/status`),

  /** Setup Telegram webhook (registers bot with Telegram) */
  setupTelegramWebhook: (channelConnectionId: number) =>
    api.post<{ data: { ok: true; botUsername: string } }>(`channel-connection/${channelConnectionId}/telegram/setup-webhook`),

  /** Disconnect Telegram bot (removes webhook) */
  disconnectTelegram: (channelConnectionId: number) =>
    api.post<{ data: { ok: true } }>(`channel-connection/${channelConnectionId}/telegram/disconnect`),

  /** Update channel connection settings (ticketConfig, defaultQueueId, config) */
  update: (channelConnectionId: number, data: UpdateChannelConnectionRequest) =>
    api.patch<{ data: ChannelConnectionResponse }>(`channel-connection/${channelConnectionId}`, data),

  /** Verify email connection (tests SMTP and IMAP connectivity) */
  verifyEmailConnection: (channelConnectionId: number) =>
    api.post<{ data: VerifyEmailConnectionResult }>(`channel-connection/${channelConnectionId}/email/verify`),
};

// ============================================
// MESSAGE CUSTOM METHODS
// ============================================

export interface ReplyMessageRequest {
  conversationUuid: string;
  quotedMessageId: number;
  body?: string;
  type?: 'text' | 'image' | 'video' | 'audio' | 'document';
  attachments?: Array<{ url: string; fileName?: string; mimeType?: string }>;
  signMessage?: boolean;
}

export interface ForwardMessageRequest {
  messageId: number;
  targetConversationId: number;
}

export interface ReactToMessageRequest {
  emoji: string;
}

export const messageApi = {
  /** Reply to a message (with quote) */
  reply: (data: ReplyMessageRequest) =>
    api.post('message/reply', data),

  /** Forward message to another conversation */
  forward: (data: ForwardMessageRequest) =>
    api.post('message/forward', data),

  /** React to a message with emoji */
  react: (messageId: number, data: ReactToMessageRequest) =>
    api.post(`message/${messageId}/react`, data),

  /** Remove reaction from message (empty emoji via DELETE) */
  removeReaction: (messageId: number) =>
    api.delete(`message/${messageId}/react`),

  /** Delete a message (soft delete by default, pass hard=true for hard delete) */
  delete: (messageId: number, hardDelete = false) =>
    api.delete(`message/${messageId}${hardDelete ? '?hard=true' : ''}`),

  /** Edit a message body */
  edit: (messageId: number, body: string) =>
    api.patch(`message/${messageId}`, { body }),
};

// ============================================
// CUSTOM DATA PROVIDER METHODS
// ============================================

/**
 * Custom methods to extend the dataProvider
 *
 * Usage with React Admin:
 * ```tsx
 * const dataProvider = useDataProvider();
 * await dataProvider.ticketListByTab({ tab: TicketListTab.ATTENDING });
 * ```
 *
 * Usage with useMutation:
 * ```tsx
 * const [transfer] = useMutation({
 *   mutationFn: (data) => dataProvider.ticketTransfer(ticketId, data)
 * });
 * ```
 */
export const customDataProviderMethods = {
  // Ticket methods
  ticketListByTab: ticketApi.listByTab,
  ticketTransfer: ticketApi.transfer,
  ticketReturnToPending: ticketApi.returnToPending,
  ticketSpy: ticketApi.spy,
  ticketCreateFromContact: ticketApi.createFromContact,
  ticketClose: ticketApi.close,
  ticketResolve: ticketApi.resolve,
  ticketGetSettings: ticketApi.getSettings,
  ticketUpdateSettings: ticketApi.updateSettings,

  // Channel connection methods (Zapy)
  channelConnectionGetZapyStatus: channelConnectionApi.getZapyStatus,
  channelConnectionGetZapyQRCode: channelConnectionApi.getZapyQRCode,
  channelConnectionRestartZapy: channelConnectionApi.restartZapyInstance,
  channelConnectionLogoutZapy: channelConnectionApi.logoutZapyInstance,

  // Channel connection methods (Telegram)
  channelConnectionGetTelegramBotStatus: channelConnectionApi.getTelegramBotStatus,
  channelConnectionSetupTelegramWebhook: channelConnectionApi.setupTelegramWebhook,
  channelConnectionDisconnectTelegram: channelConnectionApi.disconnectTelegram,

  // Channel connection update method
  channelConnectionUpdate: channelConnectionApi.update,

  // Channel connection email verify
  channelConnectionVerifyEmail: channelConnectionApi.verifyEmailConnection,

  // Message methods
  messageReply: messageApi.reply,
  messageForward: messageApi.forward,
  messageReact: messageApi.react,
  messageRemoveReaction: messageApi.removeReaction,
  messageDelete: messageApi.delete,
  messageEdit: messageApi.edit,

  // File methods
  ...fileDataProviderMethods,
};

export default {
  ticket: ticketApi,
  channelConnection: channelConnectionApi,
  message: messageApi,
  file: fileApi,
};
